import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { STATUS_META } from "./MaximoWorkOrders";
import {
  CircleNotch, ArrowLeft, Buildings, Database, ArrowsLeftRight,
  Wrench, CheckCircle, Receipt, ClockCounterClockwise,
} from "@phosphor-icons/react";

const DIR_META = {
  maximo_to_hub: { label: "Maximo → Hub", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
  transform: { label: "Hub · map", cls: "bg-neutral-200 text-neutral-700" },
  hub_to_aroflo: { label: "Hub → AroFlo", cls: "bg-[#059669]/10 text-[#059669]" },
  aroflo_to_hub: { label: "AroFlo → Hub", cls: "bg-[#059669]/10 text-[#059669]" },
  hub_to_maximo: { label: "Hub → Maximo", cls: "bg-[#0055FF]/10 text-[#0055FF]" },
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`);

function Card({ icon: Icon, title, accent, children, testid }) {
  return (
    <section className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-5 py-3">
        <Icon size={17} weight="bold" className={accent || "text-neutral-500"} />
        <h2 className="text-sm font-bold text-[#0A0A0A]">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function KV({ data }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 border-b border-neutral-100 py-1.5 text-sm">
          <dt className="text-neutral-500">{k}</dt>
          <dd className="text-right font-medium text-[#0A0A0A]">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function MaximoWorkOrderDetail() {
  const { wonum } = useParams();
  const [wo, setWo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/maximo/workorders/${wonum}`).then((r) => setWo(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [wonum]);

  if (err) return <Layout><div className="py-20 text-center text-neutral-500" data-testid="wo-detail-error">{err} · <Link to="/maximo" className="text-[#0055FF]">Back to Maximo</Link></div></Layout>;
  if (!wo) return <Layout><div className="flex h-60 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;

  const m = STATUS_META[wo.status] || { label: wo.status, cls: "bg-neutral-100" };
  const up = wo.aroflo_updates;

  return (
    <Layout>
      <div data-testid="maximo-wo-detail">
        <Link to="/maximo" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-500 hover:text-[#0055FF]" data-testid="wo-detail-back">
          <ArrowLeft size={15} weight="bold" /> Work orders
        </Link>

        {/* Header */}
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Work order audit</p>
            <h1 className="mt-1 font-heading text-4xl font-black tracking-tighter text-[#0A0A0A]">WO {wo.wonum}</h1>
            <p className="mt-1 max-w-2xl text-base text-neutral-600">{wo.description_long}</p>
          </div>
          <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${m.cls}`} data-testid="wo-detail-status">{m.label}</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ["Client", wo.client],
            ["Location", wo.location_long],
            ["Priority", wo.priority],
            ["Max W/O cost", money(wo.max_wo_cost)],
            ["AroFlo task", wo.aroflo_task_id || "—"],
            ["Allocated to", wo.allocated_to || "—"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-sm border border-neutral-200 bg-white p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">{k}</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-[#0A0A0A]" title={String(v)}>{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Audit timeline */}
          <div className="lg:col-span-1">
            <Card icon={ClockCounterClockwise} title="Audit timeline (sent & received)" accent="text-[#0055FF]" testid="wo-audit-timeline">
              <ol className="space-y-4">
                {wo.events.map((e, i) => {
                  const d = DIR_META[e.direction] || { label: e.direction, cls: "bg-neutral-100" };
                  return (
                    <li key={`${e.at}-${e.direction}-${i}`} className="relative pl-5" data-testid={`wo-event-${i}`}>
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-[#0055FF]" />
                      {i < wo.events.length - 1 && <span className="absolute left-[3px] top-3.5 h-full w-px bg-neutral-200" />}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${d.cls}`}>{d.label}</span>
                        <span className="text-[11px] text-neutral-400">{fmtDT(e.at)}</span>
                      </div>
                      <p className="mt-1 text-sm text-neutral-700">{e.message}</p>
                    </li>
                  );
                })}
              </ol>
            </Card>
          </div>

          {/* Data panels */}
          <div className="space-y-6 lg:col-span-2">
            <Card icon={Buildings} title="① Inbound — received from Maximo" accent="text-[#0055FF]" testid="wo-inbound">
              <KV data={wo.inbound_maximo} />
            </Card>

            <Card icon={ArrowsLeftRight} title="② Field mapping applied (Maximo → AroFlo)" testid="wo-mapping">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
                    <th className="py-2 pr-4 font-bold text-[#0055FF]">Maximo element</th>
                    <th className="py-2 pr-4 font-bold text-[#059669]">AroFlo field</th>
                    <th className="py-2 font-bold text-neutral-600">Value sent</th>
                  </tr></thead>
                  <tbody>
                    {wo.mapping.map((row, i) => (
                      <tr key={`${row.maximo}-${row.aroflo}`} className="border-b border-neutral-100 last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs text-[#0055FF]">{row.maximo}</td>
                        <td className="py-2 pr-4 font-mono text-xs font-semibold text-[#059669]">{row.aroflo}</td>
                        <td className="py-2 text-neutral-700">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {wo.aroflo_task ? (
              <Card icon={Database} title="③ Sent to AroFlo — task created" accent="text-[#059669]" testid="wo-aroflo-task">
                <KV data={wo.aroflo_task} />
              </Card>
            ) : (
              <Card icon={Database} title="③ Sent to AroFlo — task created" accent="text-neutral-400" testid="wo-aroflo-task">
                <p className="text-sm text-neutral-400">Not yet pushed to AroFlo.</p>
              </Card>
            )}

            {up && (
              <Card icon={Wrench} title="④ Received back from AroFlo — delivery data" accent="text-[#059669]" testid="wo-aroflo-updates">
                <div className="mb-4 flex flex-wrap gap-3">
                  <span className="rounded-sm bg-[#059669]/10 px-3 py-1 text-xs font-semibold text-[#059669]">Status: {up.aroflo_status}</span>
                  <span className="rounded-sm bg-neutral-100 px-3 py-1 text-xs font-semibold">Allocated: {up.allocated_to}</span>
                </div>

                {up.worklogs?.length > 0 && (
                  <div className="mb-4">
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">Work logs</p>
                    {up.worklogs.map((w, i) => (
                      <div key={`${w.at}-${w.by}-${i}`} className="border-b border-neutral-100 py-1.5 text-sm last:border-0">
                        <span className="text-xs text-neutral-400">{fmtDT(w.at)} · {w.by}</span>
                        <p className="text-neutral-700">{w.note}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  {up.labour?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">Labour</p>
                      {up.labour.map((l, i) => (
                        <p key={`${l.technician}-${l.charge_code}-${i}`} className="text-sm text-neutral-700">{l.technician} · {l.hours}h @ {money(l.rate)} <span className="text-xs text-neutral-400">({l.charge_code})</span></p>
                      ))}
                    </div>
                  )}
                  {up.materials?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">Materials</p>
                      {up.materials.map((mt, i) => (
                        <p key={`${mt.item}-${i}`} className="text-sm text-neutral-700">{mt.item} · ×{mt.qty} · {money(mt.unit_cost)} <span className="text-xs text-neutral-400">{mt.chargeable ? "chargeable" : "warranty"}</span></p>
                      ))}
                    </div>
                  )}
                </div>

                {up.costs && (
                  <div className="mt-4 flex flex-wrap gap-4 rounded-sm bg-neutral-50 p-3 text-sm">
                    <span>Labour <strong>{money(up.costs.labour)}</strong></span>
                    <span>Materials <strong>{money(up.costs.materials)}</strong></span>
                    <span>Expense <strong>{money(up.costs.expense)}</strong></span>
                    <span className="text-[#059669]">Total <strong>{money(up.costs.total)}</strong></span>
                  </div>
                )}
              </Card>
            )}

            {wo.quote && (
              <Card icon={Receipt} title="Quote / additional works" accent="text-[#D97706]" testid="wo-quote">
                <KV data={{ "Quote #": wo.quote.quote_no, "Amount": money(wo.quote.amount), "Status": wo.quote.status, "Scope": wo.quote.scope }} />
              </Card>
            )}

            {wo.outbound_maximo ? (
              <Card icon={CheckCircle} title="⑤ Sent back to Maximo — reconciliation" accent="text-[#0055FF]" testid="wo-outbound">
                <KV data={Object.fromEntries(Object.entries(wo.outbound_maximo).map(([k, v]) => [
                  k.replace(/_/g, " "),
                  k === "actuals" && v ? money(v.total) : (v == null ? "—" : (typeof v === "object" ? JSON.stringify(v) : v)),
                ]))} />
              </Card>
            ) : (
              <Card icon={CheckCircle} title="⑤ Sent back to Maximo — reconciliation" accent="text-neutral-400" testid="wo-outbound">
                <p className="text-sm text-neutral-400">Nothing reported back to Maximo yet.</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
